import { Package } from '../../../shared/types';
import {
  OrderRequestExpandedPackage,
  OrderRequestWashType,
} from '../models/notification-order-request.models';
import { normalizeOrderRequestWashType } from './notification-order-request-wash-type.util';
import { resolveThemeBackground } from '../../../shared/utils/theme-color.util';

/**
 * Safe CSS background for Order Request / Packages-tab theme fill.
 * Accepts legacy `theme-1`…`theme-10` or allowlisted hex/gradient; null when unset/unsupported.
 */
export function normalizePackageThemeColor(
  themeColor: string | null | undefined
): string | null {
  return resolveThemeBackground(themeColor);
}

export function filterActiveCarWashPackages(packages: Package[]): Package[] {
  return packages.filter((pkg) => pkg.type === 'car_wash' && pkg.package_status === true);
}

/**
 * Expand API packages into selectable rows (singles first, then monthlies; API order preserved).
 */
export function expandOrderRequestPackages(
  packages: Package[],
  options?: { filterPackageId?: string }
): OrderRequestExpandedPackage[] {
  let filteredPackages = packages;
  const filterPackageId = options?.filterPackageId?.trim();
  if (filterPackageId) {
    filteredPackages = packages.filter((p) => p.id === filterPackageId);
  }

  const singles: OrderRequestExpandedPackage[] = [];
  const monthlies: OrderRequestExpandedPackage[] = [];

  const pushExpanded = (
    bucket: OrderRequestExpandedPackage[],
    pkg: Package,
    washType: string,
    normalized: OrderRequestWashType
  ): void => {
    if (normalized !== 'single_wash' && normalized !== 'monthly_membership') {
      return;
    }
    const price =
      normalized === 'single_wash' ? pkg.single_list_price || '' : pkg.monthly_list_price || '';
    bucket.push({
      package: pkg,
      washType,
      displayName: pkg.package_name,
      price,
      uniqueId: `${pkg.id}_${washType}`,
      allowsCustomPrice: normalized === 'single_wash' && pkg.allows_custom_price === true,
    });
  };

  const collect = (target: 'single_wash' | 'monthly_membership', bucket: OrderRequestExpandedPackage[]): void => {
    for (const pkg of filteredPackages) {
      const washTypes = pkg.wash_types || [];
      if (washTypes.length === 0) {
        continue;
      }
      if (washTypes.length === 1) {
        const washType = washTypes[0];
        const normalized = normalizeOrderRequestWashType(washType);
        if (normalized === target) {
          pushExpanded(bucket, pkg, washType, normalized);
        }
      } else if (washTypes.length === 2) {
        for (const washType of washTypes) {
          const normalized = normalizeOrderRequestWashType(washType);
          if (normalized === target) {
            pushExpanded(bucket, pkg, washType, normalized);
          }
        }
      }
    }
  };

  collect('single_wash', singles);
  collect('monthly_membership', monthlies);

  return [...singles, ...monthlies];
}
